import * as React from 'react';
import { Loader2, Plus, Trash2, Pencil } from 'lucide-react';
import type { EventTypeUpsertInput, TimeBlock } from '@cp3/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input, Textarea } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/toast';
import {
  useDeleteEventType,
  useEventTypes,
  useUpsertEventType,
  type EventTypeRow,
} from '../../hooks/useEventTypes';
import { cn } from '../../lib/utils';

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'] as const;

export function EventTypesPage() {
  const events = useEventTypes();
  const [editing, setEditing] = React.useState<EventTypeRow | 'new' | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditing('new')}>
          <Plus className="mr-2 h-4 w-4" /> Neuer Termin-Typ
        </Button>
      </div>

      {events.isLoading ? (
        <div className="flex h-32 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
        </div>
      ) : (events.data ?? []).length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Noch keine Termin-Typen. Klicke „Neuer Termin-Typ", um z.B. „Erstberatung" anzulegen.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {(events.data ?? []).map((ev) => (
            <EventTypeCard key={ev.id} item={ev} onEdit={() => setEditing(ev)} />
          ))}
        </div>
      )}

      {editing && (
        <EditDialog
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function EventTypeCard({ item, onEdit }: { item: EventTypeRow; onEdit: () => void }) {
  const remove = useDeleteEventType();
  const { toast } = useToast();

  return (
    <Card className={cn(!item.active && 'opacity-60')}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: item.color }} />
              <CardTitle>{item.name}</CardTitle>
            </div>
            <CardDescription>
              {item.durationMinutes} min · {item.bufferMinutes} min Puffer · {item.timeBlocks.length} Zeitblöcke
            </CardDescription>
          </div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={async () => {
                if (!confirm(`Termin-Typ "${item.name}" wirklich löschen?`)) return;
                await remove.mutateAsync(item.id);
                toast({ title: 'Gelöscht' });
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground">{item.aiDescription}</p>
        {item.aiKeywords.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {item.aiKeywords.map((k) => (
              <span key={k} className="rounded bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider">
                {k}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 space-y-1">
          {item.timeBlocks.map((b, i) => (
            <div key={i} className="text-xs text-muted-foreground">
              {WEEKDAYS[b.weekday]}: {minutesToHHMM(b.startMinutes)}–{minutesToHHMM(b.endMinutes)}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function EditDialog({ initial, onClose }: { initial: EventTypeRow | null; onClose: () => void }) {
  const upsert = useUpsertEventType();
  const { toast } = useToast();
  const isNew = !initial;

  const [form, setForm] = React.useState<EventTypeUpsertInput>(() => initial ?? blankEventType());
  const [keywordsText, setKeywordsText] = React.useState(form.aiKeywords.join(', '));

  function patch<K extends keyof EventTypeUpsertInput>(key: K, value: EventTypeUpsertInput[K]) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  function patchBlock(idx: number, p: Partial<TimeBlock>) {
    setForm((prev) => {
      const next = [...prev.timeBlocks];
      const cur = next[idx];
      if (!cur) return prev;
      next[idx] = { ...cur, ...p };
      return { ...prev, timeBlocks: next };
    });
  }

  function addBlock() {
    setForm((p) => ({
      ...p,
      timeBlocks: [...p.timeBlocks, { weekday: 1, startMinutes: 9 * 60, endMinutes: 12 * 60 }],
    }));
  }

  function removeBlock(idx: number) {
    setForm((p) => ({ ...p, timeBlocks: p.timeBlocks.filter((_, i) => i !== idx) }));
  }

  async function onSave() {
    const keywords = keywordsText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 20);
    try {
      await upsert.mutateAsync({ ...form, aiKeywords: keywords });
      toast({ title: isNew ? 'Termin-Typ erstellt' : 'Aktualisiert' });
      onClose();
    } catch (err) {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Neuer Termin-Typ' : `Bearbeiten: ${form.name || form.id}`}</DialogTitle>
          <DialogDescription>
            Der Voice-Agent stützt sich auf Name + Beschreibung + Keywords um den richtigen Typ zu wählen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_120px_120px]">
            <div className="space-y-1.5">
              <Label htmlFor="et-name">Name</Label>
              <Input
                id="et-name"
                value={form.name}
                placeholder="z.B. Erstberatung"
                onChange={(e) => {
                  patch('name', e.target.value);
                  if (isNew) patch('id', slugify(e.target.value));
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-dur">Dauer (min)</Label>
              <Input
                id="et-dur"
                type="number"
                min={5}
                max={480}
                value={form.durationMinutes}
                onChange={(e) => patch('durationMinutes', Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-buf">Puffer (min)</Label>
              <Input
                id="et-buf"
                type="number"
                min={0}
                max={120}
                value={form.bufferMinutes}
                onChange={(e) => patch('bufferMinutes', Number(e.target.value))}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
            <div className="space-y-1.5">
              <Label htmlFor="et-id">ID (technisch)</Label>
              <Input
                id="et-id"
                value={form.id}
                onChange={(e) => patch('id', e.target.value)}
                disabled={!isNew}
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="et-color">Farbe</Label>
              <Input
                id="et-color"
                type="color"
                value={form.color}
                onChange={(e) => patch('color', e.target.value)}
                className="h-10 w-full p-1"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="et-desc">KI-Beschreibung</Label>
            <Textarea
              id="et-desc"
              rows={3}
              value={form.aiDescription}
              onChange={(e) => patch('aiDescription', e.target.value)}
              placeholder="Wird im System-Prompt eingeblendet."
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="et-kw">KI-Schlüsselwörter (Komma-getrennt)</Label>
            <Input
              id="et-kw"
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              placeholder="erstgespräch, kennenlernen, neukunde"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>Zeitblöcke</Label>
              <Button size="sm" variant="outline" onClick={addBlock}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Block hinzufügen
              </Button>
            </div>
            <div className="space-y-2">
              {form.timeBlocks.map((b, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-[160px_120px_120px_auto]">
                  <select
                    value={b.weekday}
                    onChange={(e) => patchBlock(i, { weekday: Number(e.target.value) as TimeBlock['weekday'] })}
                    className="input-base"
                  >
                    {WEEKDAYS.map((d, idx) => (
                      <option key={idx} value={idx}>{d}</option>
                    ))}
                  </select>
                  <Input
                    type="time"
                    value={minutesToHHMM(b.startMinutes)}
                    onChange={(e) => patchBlock(i, { startMinutes: hhmmToMinutes(e.target.value) })}
                  />
                  <Input
                    type="time"
                    value={minutesToHHMM(b.endMinutes)}
                    onChange={(e) => patchBlock(i, { endMinutes: hhmmToMinutes(e.target.value) })}
                  />
                  <Button size="icon" variant="ghost" onClick={() => removeBlock(i)} className="h-9 w-9 text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {form.timeBlocks.length === 0 && (
                <p className="text-sm text-muted-foreground">Mindestens ein Zeitblock ist erforderlich.</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <Label>Aktiv</Label>
            <Switch checked={form.active} onCheckedChange={(v) => patch('active', v)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={onSave} disabled={upsert.isPending || !form.name || !form.id || form.timeBlocks.length === 0}>
            {upsert.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function blankEventType(): EventTypeUpsertInput {
  return {
    id: '',
    name: '',
    durationMinutes: 30,
    bufferMinutes: 5,
    color: '#5eead4',
    timeBlocks: [{ weekday: 1, startMinutes: 9 * 60, endMinutes: 12 * 60 }],
    aiKeywords: [],
    aiDescription: '',
    active: true,
  };
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function minutesToHHMM(m: number): string {
  const h = Math.floor(m / 60).toString().padStart(2, '0');
  const mm = (m % 60).toString().padStart(2, '0');
  return `${h}:${mm}`;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  return Number(h ?? 0) * 60 + Number(m ?? 0);
}
